import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import {
    PRIMARY_TERMS,
    autoApplyPromotionReviews,
    fetchPromotionReviews,
    savePromotionReviews,
} from "../../utils/termPromotionReviewApi";

const PASS_OPTIONS = [
    { value: "", label: "Not set" },
    { value: "pass", label: "Pass" },
    { value: "fail", label: "Fail" },
];

function passValueToBool(value) {
    if (value === "pass") return true;
    if (value === "fail") return false;
    return null;
}

function boolToPassValue(passed) {
    if (passed === true) return "pass";
    if (passed === false) return "fail";
    return "";
}

function EligibilityChip({ eligibility }) {
    if (!eligibility) return null;
    if (eligibility.eligible) {
        return <Chip size="small" color="success" label="Eligible to promote" />;
    }
    return <Chip size="small" color="warning" label={eligibility.reason || "Not eligible"} />;
}

function TermMarkHint({ termRow }) {
    if (termRow.averageMark == null && termRow.caAverage == null && termRow.examAverage == null) {
        return null;
    }
    return (
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
            {termRow.averageMark != null ? `${Number(termRow.averageMark).toFixed(0)}%` : "—"}
            {termRow.caAverage != null || termRow.examAverage != null ? (
                <>
                    {" "}
                    (CA {termRow.caAverage != null ? Number(termRow.caAverage).toFixed(0) : "—"} / Exam{" "}
                    {termRow.examAverage != null ? Number(termRow.examAverage).toFixed(0) : "—"})
                </>
            ) : null}
            {termRow.suggestedPass != null
                ? ` · suggests ${termRow.suggestedPass ? "pass" : "fail"}`
                : ""}
        </Typography>
    );
}

/** Class teacher pass/fail per term for year-end promotion (Primary & Secondary). */
export default function PrimaryTermPromotionReview({
    schoolId,
    classId = null,
    studentId = null,
    academicYear,
    teacherId = null,
    compact = false,
    onSaved,
}) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [rows, setRows] = useState([]);
    const [draft, setDraft] = useState({});
    const [track, setTrack] = useState("primary");
    const [termsList, setTermsList] = useState(PRIMARY_TERMS);

    const load = useCallback(async () => {
        if (!schoolId || (!classId && !studentId)) return;
        setLoading(true);
        setError("");
        try {
            const data = await fetchPromotionReviews(schoolId, { classId, studentId, academicYear });
            const students = Array.isArray(data.students) ? data.students : [];
            setRows(students);
            setTrack(data.track || "primary");
            setTermsList(Array.isArray(data.terms) && data.terms.length ? data.terms : PRIMARY_TERMS);
            const next = {};
            for (const row of students) {
                for (const termRow of row.terms || []) {
                    next[`${row.studentId}|${termRow.term}`] = boolToPassValue(termRow.passed);
                }
            }
            setDraft(next);
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Could not load promotion reviews.");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [schoolId, classId, studentId, academicYear]);

    useEffect(() => {
        load();
    }, [load]);

    const dirtyReviews = useMemo(() => {
        const reviews = [];
        for (const row of rows) {
            for (const termRow of row.terms || []) {
                const key = `${row.studentId}|${termRow.term}`;
                const value = draft[key];
                const passed = passValueToBool(value);
                if (passed === true || passed === false) {
                    reviews.push({
                        studentId: row.studentId,
                        term: termRow.term,
                        passed,
                        averageMark: termRow.averageMark,
                        caAverage: termRow.caAverage,
                        examAverage: termRow.examAverage,
                    });
                }
            }
        }
        return reviews;
    }, [rows, draft]);

    const handleSave = async () => {
        if (!dirtyReviews.length) {
            setMessage("Set pass or fail for at least one term before saving.");
            return;
        }
        setSaving(true);
        setError("");
        setMessage("");
        try {
            const result = await savePromotionReviews(schoolId, {
                academicYear,
                teacherId,
                reviews: dirtyReviews,
            });
            setMessage(result.message || "Saved.");
            await load();
            onSaved?.();
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Could not save.");
        } finally {
            setSaving(false);
        }
    };

    const handleAutoApply = async () => {
        if (!classId) {
            setMessage("Auto-apply requires a class context.");
            return;
        }
        const ok = window.confirm(
            "Apply automated pass/fail from CA (40%) + exam (60%) weighted averages for all pupils in this class?",
        );
        if (!ok) return;
        setApplying(true);
        setError("");
        setMessage("");
        try {
            const result = await autoApplyPromotionReviews(schoolId, {
                academicYear,
                classId,
                teacherId,
            });
            setMessage(result.message || "Suggestions applied.");
            await load();
            onSaved?.();
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Could not auto-apply.");
        } finally {
            setApplying(false);
        }
    };

    const setDraftValue = (studentIdKey, term, value) => {
        setDraft((prev) => ({ ...prev, [`${studentIdKey}|${term}`]: value }));
    };

    const trackLabel = track === "secondary" ? "Secondary" : "Primary";
    const guidance =
        track === "secondary"
            ? "Secondary: Term 3 pass required, year average ≥50%, and at most 3 subjects below 40%. Weighted term mark = 40% CA + 60% exam."
            : "Primary: pupils need at least 2 term passes including Term 3 to move up; others repeat the same class.";

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    const actionButtons = (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {classId ? (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AutoFixHighOutlinedIcon />}
                    disabled={applying || saving}
                    onClick={handleAutoApply}
                >
                    {applying ? "Applying…" : "Apply suggestions"}
                </Button>
            ) : null}
            <Button
                variant="contained"
                size="small"
                startIcon={<SaveOutlinedIcon />}
                disabled={saving || applying}
                onClick={handleSave}
            >
                {saving ? "Saving…" : "Save term results"}
            </Button>
        </Stack>
    );

    if (compact && rows.length === 1) {
        const row = rows[0];
        return (
            <Box sx={{ mt: 2 }} className="no-print">
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Year-end promotion ({trackLabel})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {guidance}
                </Typography>
                {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
                {message ? <Alert severity="success" sx={{ mb: 1 }}>{message}</Alert> : null}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 1.5 }}>
                    {termsList.map((term) => {
                        const termRow = (row.terms || []).find((t) => t.term === term) || { term };
                        const key = `${row.studentId}|${term}`;
                        return (
                            <FormControl key={term} size="small" sx={{ minWidth: 140 }}>
                                <Select
                                    displayEmpty
                                    value={draft[key] ?? ""}
                                    onChange={(e) => setDraftValue(row.studentId, term, e.target.value)}
                                >
                                    {PASS_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value || "unset"} value={opt.value}>
                                            {term}: {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <TermMarkHint termRow={termRow} />
                            </FormControl>
                        );
                    })}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <EligibilityChip eligibility={row.eligibility} />
                    {track === "secondary" && row.yearAverage != null ? (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`Year avg ${Number(row.yearAverage).toFixed(0)}% · ${row.subjectsBelowMin || 0} subj. below 40%`}
                        />
                    ) : null}
                    <Box sx={{ flex: 1 }} />
                    {actionButtons}
                </Stack>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                {trackLabel} year-end promotion review
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {guidance}
            </Typography>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            {message ? <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert> : null}
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Roll</TableCell>
                        <TableCell>Name</TableCell>
                        {termsList.map((term) => (
                            <TableCell key={term} align="center">
                                {term}
                            </TableCell>
                        ))}
                        {track === "secondary" ? <TableCell align="center">Year stats</TableCell> : null}
                        <TableCell>Outcome</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.studentId}>
                            <TableCell>{row.rollNum}</TableCell>
                            <TableCell>{row.name}</TableCell>
                            {termsList.map((term) => {
                                const termRow = (row.terms || []).find((t) => t.term === term) || { term };
                                const key = `${row.studentId}|${term}`;
                                return (
                                    <TableCell key={term} align="center">
                                        <Select
                                            size="small"
                                            displayEmpty
                                            value={draft[key] ?? ""}
                                            onChange={(e) => setDraftValue(row.studentId, term, e.target.value)}
                                            sx={{ minWidth: 100 }}
                                        >
                                            {PASS_OPTIONS.map((opt) => (
                                                <MenuItem key={opt.value || "unset"} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <TermMarkHint termRow={termRow} />
                                    </TableCell>
                                );
                            })}
                            {track === "secondary" ? (
                                <TableCell align="center">
                                    <Typography variant="caption" display="block">
                                        Avg: {row.yearAverage != null ? `${Number(row.yearAverage).toFixed(0)}%` : "—"}
                                    </Typography>
                                    <Typography variant="caption" display="block" color="text.secondary">
                                        &lt;40%: {row.subjectsBelowMin ?? 0}
                                    </Typography>
                                </TableCell>
                            ) : null}
                            <TableCell>
                                <EligibilityChip eligibility={row.eligibility} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                {actionButtons}
            </Stack>
        </Box>
    );
}
